import type { Event } from "@/lib/event-schema";
import type { SiteDonationConfig } from "@/lib/site-schema";

type CheckoutSurface = "event" | "site";

type CheckoutRequest = {
  surface: CheckoutSurface;
  amountCents: number;
  eventSlug?: string;
  campaignId?: string;
  clientReference?: string;
};

export type PotluckPublicItem = {
  id: string;
  timestamp: number;
  name: string;
  contributorName: string;
  category: string;
  quantity: string;
  notes?: string;
};

export type RsvpCounts = {
  families: number;
  adults: number;
  children: number;
  attendees: number;
};

export type CompetitionPublicEntry = {
  id: string;
  timestamp: number;
  [key: string]: unknown;
};

export type AdminRsvpEntry = {
  id: string;
  timestamp: number;
  familyName: string;
  adults: number;
  children: number;
  /** Age range per child, one entry per child (e.g. ["3-6", "7-10"]). */
  childrenAges?: string[];
};

export type AdminPotluckItem = {
  id: string;
  timestamp: number;
  name: string;
  contributorName: string;
  category: string;
  quantity: string;
  notes?: string;
};

export type AdminCompetitionEntry = {
  id: string;
  timestamp: number;
  [key: string]: unknown;
};

export type AdminStats = {
  rsvp: { families: number; adults: number; children: number; attendees: number };
  potluck: { dishes: number; byCategory: Record<string, number> };
  competition: { entries: number };
  donations: {
    totalCents: number;
    manualCents: number;
    stripeCents: number;
    manualCount: number;
  };
};

export type ManualDonationMethod = "zelle" | "cash" | "bank" | "check" | "other";

export type AdminManualDonation = {
  id: string;
  timestamp: number;
  amountCents: number;
  method: ManualDonationMethod;
  donorName?: string;
  note?: string;
  occurredOn?: string;
  campaignId: string;
};

export type DonationSource = "stripe" | ManualDonationMethod;

export type AdminDonationEntry = {
  id: string;
  timestamp: number;
  amountCents: number;
  source: DonationSource;
  status?: "completed" | "refunded";
  donorName?: string;
  donorEmail?: string;
  note?: string;
  occurredOn?: string;
  campaignId: string;
  deletable: boolean;
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: "same-origin", ...init });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${res.status} ${res.statusText}: ${text || path}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

const base = (slug: string) => `/api/events/${encodeURIComponent(slug)}`;

export const api = {
  events: {
    list: () =>
      request<Array<{ slug: string; title: string; status: "draft" | "published"; date?: string }>>(
        "/api/events",
      ),
    get: (slug: string) => request<Event>(`${base(slug)}/document`),
    save: (slug: string, body: Event) =>
      request<Event>(`${base(slug)}/document`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    create: (body: Partial<Event> & { slug: string }) =>
      request<Event>("/api/events", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
  },
  admin: {
    whoami: () => request<{ authed: boolean; configured: boolean }>("/api/admin/whoami"),
    login: (password: string) =>
      request<{ ok: true }>("/api/admin/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      }),
    logout: () => request<{ ok: true }>("/api/admin/logout", { method: "POST" }),
    stats: (slug: string) =>
      request<AdminStats>(`/api/admin/events/${encodeURIComponent(slug)}/stats`),
    rsvp: {
      list: (slug: string) =>
        request<AdminRsvpEntry[]>(`/api/admin/events/${encodeURIComponent(slug)}/rsvp`),
      remove: (slug: string, id: string) =>
        request<{ success: boolean }>(
          `/api/admin/events/${encodeURIComponent(slug)}/rsvp?id=${encodeURIComponent(id)}`,
          { method: "DELETE" },
        ),
    },
    potluck: {
      list: (slug: string) =>
        request<AdminPotluckItem[]>(`/api/admin/events/${encodeURIComponent(slug)}/potluck`),
      remove: (slug: string, id: string) =>
        request<{ success: boolean }>(
          `/api/admin/events/${encodeURIComponent(slug)}/potluck?id=${encodeURIComponent(id)}`,
          { method: "DELETE" },
        ),
    },
    competition: {
      list: (slug: string) =>
        request<AdminCompetitionEntry[]>(
          `/api/admin/events/${encodeURIComponent(slug)}/competition`,
        ),
      remove: (slug: string, id: string) =>
        request<{ success: boolean }>(
          `/api/admin/events/${encodeURIComponent(slug)}/competition?id=${encodeURIComponent(id)}`,
          { method: "DELETE" },
        ),
    },
    donations: {
      list: (slug: string) =>
        request<AdminDonationEntry[]>(`/api/admin/events/${encodeURIComponent(slug)}/donations`),
      add: (
        slug: string,
        body: {
          amountCents: number;
          method: ManualDonationMethod;
          donorName?: string;
          note?: string;
          occurredOn?: string;
          campaignId?: string;
        },
      ) =>
        request<AdminManualDonation>(
          `/api/admin/events/${encodeURIComponent(slug)}/donations/manual`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
          },
        ),
      remove: (slug: string, id: string) =>
        request<{ success: boolean }>(
          `/api/admin/events/${encodeURIComponent(slug)}/donations/manual?id=${encodeURIComponent(id)}`,
          { method: "DELETE" },
        ),
    },
  },
  potluck: {
    list: (slug: string) => request<PotluckPublicItem[]>(`${base(slug)}/potluck`),
    add: (
      slug: string,
      body: Omit<PotluckPublicItem, "id" | "timestamp"> & { passcode?: string },
    ) =>
      request<PotluckPublicItem>(`${base(slug)}/potluck`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    remove: (slug: string, id: string, passcode: string) =>
      request<{ success: boolean }>(`${base(slug)}/potluck/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, passcode }),
      }),
  },
  rsvp: {
    counts: (slug: string) => request<RsvpCounts>(`${base(slug)}/rsvp`),
    add: (
      slug: string,
      body: {
        familyName: string;
        adults: number;
        children: number;
        childrenAges?: string[];
        passcode?: string;
      },
    ) =>
      request<AdminRsvpEntry>(`${base(slug)}/rsvp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    check: (slug: string, familyName: string, passcode: string) =>
      request<AdminRsvpEntry>(`${base(slug)}/rsvp-check`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ familyName, passcode }),
      }),
    remove: (slug: string, id: string, passcode: string) =>
      request<{ success: boolean }>(`${base(slug)}/rsvp/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, passcode }),
      }),
    update: (
      slug: string,
      id: string,
      passcode: string,
      body: { adults: number; children: number; childrenAges?: string[] },
    ) =>
      request<AdminRsvpEntry>(`${base(slug)}/rsvp/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, passcode, ...body }),
      }),
  },
  competition: {
    list: (slug: string) => request<CompetitionPublicEntry[]>(`${base(slug)}/competition`),
    add: (slug: string, body: Record<string, unknown>) =>
      request<CompetitionPublicEntry>(`${base(slug)}/competition`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
  },
  site: {
    donation: {
      // Returns null when no admin has saved a config yet; the consumer
      // chooses how to render the "not configured" state.
      get: () => request<SiteDonationConfig | null>("/api/site/donation"),
      save: (body: SiteDonationConfig) =>
        request<SiteDonationConfig>("/api/site/donation", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }),
    },
  },
  donate: {
    // Single typed entry point for both donation surfaces (event module
    // + /donate page). Returns a Stripe Checkout URL the client must
    // redirect to. request<T> throws on non-2xx so the success body is
    // always populated; no need for optional fallback fields here.
    checkout: (body: CheckoutRequest) =>
      request<{ url: string; clientReference: string }>("/api/donate/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    totals: {
      site: () => request<{ surface: "site"; totalCents: number }>("/api/donate/totals/site"),
      event: (slug: string, campaignId: string) =>
        request<{
          surface: "event";
          slug: string;
          campaignId: string;
          totalCents: number;
        }>(
          `/api/donate/totals/event/${encodeURIComponent(slug)}/${encodeURIComponent(campaignId)}`,
        ),
    },
  },
};
